// ═══════════════════════════════════════════════════════════════════════
// interfaceTaxonomyTab.jsx — Interactive CLM Interface Taxonomy (107 flows)
// ═══════════════════════════════════════════════════════════════════════
// Browses the 9-L1 / 38-L2 / 107-L3 interface taxonomy with:
//   · Sankey-style aggregate view (L1 → L2 → severity)
//   · Filterable / searchable table
//   · Drill-in detail card with full schema (purpose, type, payload,
//     protocol, source, destination, required infra, severity, fallback)
//   · Cross-links to CLM interfaces that match (via the LLD tab)
// Source data: interfaceTaxonomy.js (built from CLM_Interface_Taxonomy.csv)
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { T, FONTS, chipBtn, pill } from "./theme";
import {
  FLOWS, L1_CATEGORIES, L2_SUBCATEGORIES, SEVERITY_META,
  INTERFACE_TYPES, PAYLOAD_TYPES, FLOW_COUNTS,
  flowById,
} from "./interfaceTaxonomy";
import { IFACES } from "./IfacesData";
import { EntityRefList } from "./navigation";

const VIEWS = [
  { id: "overview", label: "📊 Overview",          color: T.cyan },
  { id: "browse",   label: "🔍 Browse all (107)",   color: T.green },
  { id: "matrix",   label: "🗺️ L1 × Severity matrix", color: T.amber },
  { id: "infra",    label: "🔌 By interface type",  color: T.purple },
  { id: "payload",  label: "📦 By payload type",    color: T.gold },
];

export default function InterfaceTaxonomyTab() {
  const [view, setView]         = useState("overview");
  const [filterL1, setFilterL1] = useState("ALL");
  const [filterL2, setFilterL2] = useState("ALL");
  const [filterSev, setFilterSev] = useState("ALL");
  const [filterIfT, setFilterIfT] = useState("ALL");
  const [filterPT, setFilterPT] = useState("ALL");
  const [search, setSearch]     = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const filtered = useMemo(() => {
    return FLOWS.filter(f => {
      if (filterL1  !== "ALL" && f.l1  !== filterL1)  return false;
      if (filterL2  !== "ALL" && f.l2  !== filterL2)  return false;
      if (filterSev !== "ALL" && f.severity !== Number(filterSev)) return false;
      if (filterIfT !== "ALL" && f.interfaceType !== filterIfT) return false;
      if (filterPT  !== "ALL" && f.payloadType  !== filterPT)  return false;
      if (search) {
        const hay = `${f.l3} ${f.examples} ${f.protocol} ${f.source} ${f.destination}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [filterL1, filterL2, filterSev, filterIfT, filterPT, search]);

  // When L1 changes, reset L2
  const setL1AndReset = (v) => { setFilterL1(v); setFilterL2("ALL"); };

  const selected = selectedId ? flowById(selectedId) : null;
  const l2Options = filterL1 === "ALL" ? [] : (L2_SUBCATEGORIES[filterL1] || []);

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🗂 CLM Interface Taxonomy · 107 flows
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          Every kind of thing that must cross a boundary in a manufacturing operation, classified by purpose,
          type, payload, severity, and required infrastructure.
        </div>
        <div style={{ fontSize: 9.5, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
          "If something needs to cross a boundary, this taxonomy classifies it. If your interface isn't here, you're either missing one — or you've invented a new category."
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          { l: "Total flows",       v: FLOW_COUNTS.total,             c: T.cyan },
          { l: "L1 categories",     v: L1_CATEGORIES.length,          c: T.purple },
          { l: "Interface types",   v: Object.keys(INTERFACE_TYPES).length, c: T.amber },
          { l: "Payload types",     v: Object.keys(PAYLOAD_TYPES).length,   c: T.gold },
          { l: "Sev 5 (critical)",  v: FLOW_COUNTS.bySeverity[5],     c: T.red },
          { l: "Sev 4 (high)",      v: FLOW_COUNTS.bySeverity[4],     c: T.orange },
          { l: "Filtered",          v: filtered.length,               c: T.green },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "8px 14px", textAlign: "center",
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase" }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={chipBtn(view === v.id, v.color)}>
            {v.label}
          </button>
        ))}
      </div>

      {/* ─── OVERVIEW ─── */}
      {view === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {L1_CATEGORIES.map(cat => {
            const flowsInCat = FLOWS.filter(f => f.l1 === cat.id);
            const sevDistribution = [5, 4, 3, 2, 1].map(s => ({
              s,
              n: flowsInCat.filter(f => f.severity === s).length,
            }));
            return (
              <div key={cat.id}
                onClick={() => { setView("browse"); setL1AndReset(cat.id); }}
                style={{
                  background: T.bg2,
                  border: `1px solid ${cat.color}40`,
                  borderTop: `4px solid ${cat.color}`,
                  borderRadius: 8, padding: "14px 16px",
                  cursor: "pointer",
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 28 }}>{cat.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: cat.color }}>
                      {cat.id}
                    </div>
                    <div style={{ fontSize: 9, color: T.text3, fontFamily: FONTS.mono }}>
                      {flowsInCat.length} flows
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>
                  {cat.desc}
                </div>
                {/* Severity bar */}
                <div style={{ display: "flex", gap: 2, height: 6, marginBottom: 6 }}>
                  {sevDistribution.map(b => b.n > 0 && (
                    <div key={b.s} style={{
                      flex: b.n,
                      background: SEVERITY_META[b.s].color,
                      borderRadius: 1,
                    }} title={`Severity ${b.s}: ${b.n} flows`}/>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {sevDistribution.map(b => b.n > 0 && (
                    <span key={b.s} style={{
                      fontSize: 7.5, fontFamily: FONTS.mono,
                      color: SEVERITY_META[b.s].color,
                    }}>S{b.s}:{b.n}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── BROWSE ALL ─── */}
      {view === "browse" && (
        <div>
          {/* Filter bar */}
          <div style={{
            display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap",
            padding: "10px 14px", background: T.bg2, borderRadius: 6, border: `1px solid ${T.border}`,
          }}>
            <input type="text" placeholder="🔍 Search name, example, protocol..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
                padding: "5px 10px", borderRadius: 4, fontSize: 10, outline: "none", minWidth: 240,
              }}/>
            <select value={filterL1} onChange={e => setL1AndReset(e.target.value)} style={selectSty}>
              <option value="ALL">All L1 categories</option>
              {L1_CATEGORIES.map(c => (
                <option key={c.id} value={c.id}>{c.icon} {c.id} ({FLOW_COUNTS.byL1[c.id] || 0})</option>
              ))}
            </select>
            {filterL1 !== "ALL" && (
              <select value={filterL2} onChange={e => setFilterL2(e.target.value)} style={selectSty}>
                <option value="ALL">All L2</option>
                {l2Options.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <select value={filterSev} onChange={e => setFilterSev(e.target.value)} style={selectSty}>
              <option value="ALL">All severity</option>
              {[5,4,3,2,1].map(s => (
                <option key={s} value={s}>S{s} · {SEVERITY_META[s].label} ({FLOW_COUNTS.bySeverity[s]})</option>
              ))}
            </select>
            <select value={filterIfT} onChange={e => setFilterIfT(e.target.value)} style={selectSty}>
              <option value="ALL">All interface types</option>
              {Object.entries(INTERFACE_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.icon} {v.label} ({FLOW_COUNTS.byInterfaceType[k] || 0})</option>
              ))}
            </select>
            <select value={filterPT} onChange={e => setFilterPT(e.target.value)} style={selectSty}>
              <option value="ALL">All payload types</option>
              {Object.entries(PAYLOAD_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label} ({FLOW_COUNTS.byPayloadType[k] || 0})</option>
              ))}
            </select>
            <div style={{ marginLeft: "auto", fontSize: 10, color: T.text2 }}>
              {filtered.length} of {FLOW_COUNTS.total}
            </div>
          </div>

          {/* Two-pane: list left, detail right */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 420px", gap: 12 }}>
            {/* Flow list */}
            <div style={{
              background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
              maxHeight: 600, overflowY: "auto",
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                <thead style={{ position: "sticky", top: 0, background: T.bg3, zIndex: 1 }}>
                  <tr>
                    <th style={th}>ID</th>
                    <th style={th}>L1</th>
                    <th style={th}>L2</th>
                    <th style={th}>L3 flow name</th>
                    <th style={th}>I/F type</th>
                    <th style={th}>Payload</th>
                    <th style={th}>Sev</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(f => {
                    const cat = L1_CATEGORIES.find(c => c.id === f.l1);
                    const sev = SEVERITY_META[f.severity];
                    const isSel = selectedId === f.id;
                    return (
                      <tr key={f.id} onClick={() => setSelectedId(f.id)}
                        style={{
                          borderBottom: `1px solid ${T.border2}`,
                          background: isSel ? `${cat?.color}15` : "transparent",
                          cursor: "pointer",
                        }}>
                        <td style={{ ...td, fontFamily: FONTS.mono, color: T.text3 }}>{f.id}</td>
                        <td style={{ ...td, color: cat?.color, fontWeight: 600 }}>
                          {cat?.icon} {f.l1}
                        </td>
                        <td style={{ ...td, color: T.text2, fontSize: 9 }}>{f.l2}</td>
                        <td style={{ ...td, color: T.text, fontWeight: 500 }}>{f.l3}</td>
                        <td style={td}>
                          <span style={{ fontSize: 8, color: INTERFACE_TYPES[f.interfaceType]?.color }}>
                            {INTERFACE_TYPES[f.interfaceType]?.icon} {INTERFACE_TYPES[f.interfaceType]?.label}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 8, color: PAYLOAD_TYPES[f.payloadType]?.color }}>
                            {PAYLOAD_TYPES[f.payloadType]?.label}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{
                            fontFamily: FONTS.mono, fontWeight: 700, fontSize: 9,
                            padding: "1px 6px", borderRadius: 4,
                            background: `${sev.color}20`, color: sev.color,
                          }}>S{f.severity}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan="7" style={{ ...td, textAlign: "center", color: T.text3, fontStyle: "italic", padding: 24 }}>
                      No flows match the current filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Detail card */}
            <div>
              {selected ? <FlowDetail flow={selected}/> : (
                <div style={{
                  background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
                  padding: 40, textAlign: "center", color: T.text3, fontStyle: "italic", fontSize: 11,
                }}>
                  ← Click any flow to see its full schema, required infrastructure, fallback, and matching CLM interfaces.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── L1 × SEVERITY MATRIX ─── */}
      {view === "matrix" && <MatrixView setView={setView} setL1={setL1AndReset} setSev={setFilterSev}/>}

      {/* ─── BY INTERFACE TYPE ─── */}
      {view === "infra" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {Object.entries(INTERFACE_TYPES).map(([k, meta]) => {
            const flows = FLOWS.filter(f => f.interfaceType === k);
            if (flows.length === 0) return null;
            return (
              <div key={k} style={{
                background: T.bg2,
                border: `1px solid ${meta.color}40`, borderTop: `3px solid ${meta.color}`,
                borderRadius: 8, padding: "12px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>{meta.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: meta.color, fontSize: 11 }}>{meta.label}</div>
                    <div style={{ fontSize: 8.5, color: T.text3 }}>{flows.length} flow{flows.length>1?"s":""}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {flows.map(f => {
                    const cat = L1_CATEGORIES.find(c => c.id === f.l1);
                    return (
                      <button key={f.id} onClick={() => { setView("browse"); setSelectedId(f.id); }}
                        title={`${f.l3} (${f.l1})`}
                        style={{
                          fontSize: 7.5, fontFamily: FONTS.mono, padding: "1px 5px",
                          borderRadius: 3, border: `1px solid ${cat?.color || meta.color}40`,
                          background: `${cat?.color || meta.color}15`,
                          color: cat?.color || meta.color, cursor: "pointer",
                        }}>
                        #{f.id} {f.l3}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── BY PAYLOAD TYPE ─── */}
      {view === "payload" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {Object.entries(PAYLOAD_TYPES).map(([k, meta]) => {
            const flows = FLOWS.filter(f => f.payloadType === k);
            if (flows.length === 0) return null;
            return (
              <div key={k} style={{
                background: T.bg2,
                border: `1px solid ${meta.color}40`, borderLeft: `4px solid ${meta.color}`,
                borderRadius: 6, padding: "10px 12px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, color: meta.color, fontSize: 11 }}>{meta.label}</span>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: meta.color }}>{flows.length}</span>
                </div>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {flows.map(f => (
                    <button key={f.id} onClick={() => { setView("browse"); setSelectedId(f.id); }}
                      style={{
                        fontSize: 7.5, fontFamily: FONTS.mono, padding: "1px 5px",
                        borderRadius: 3, border: `1px solid ${meta.color}40`,
                        background: `${meta.color}10`,
                        color: meta.color, cursor: "pointer",
                      }}>
                      #{f.id}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// FlowDetail · the full schema card
// ───────────────────────────────────────────────────────────────────────

function FlowDetail({ flow }) {
  const cat = L1_CATEGORIES.find(c => c.id === flow.l1);
  const sev = SEVERITY_META[flow.severity];
  const ifT = INTERFACE_TYPES[flow.interfaceType];
  const pT  = PAYLOAD_TYPES[flow.payloadType];

  // Heuristic: find CLM interfaces in IFACES whose name mentions this flow's L3 or examples.
  const matchingInterfaces = useMemo(() => {
    const needles = [flow.l3, ...flow.examples.split(",").map(s => s.trim()).slice(0, 3)]
      .map(s => s.toLowerCase());
    return IFACES.filter(i => {
      const hay = `${i.name} ${i.bo || ""} ${i.from || ""} ${i.to || ""}`.toLowerCase();
      return needles.some(n => n.length > 4 && hay.includes(n));
    }).slice(0, 6).map(i => i.id);
  }, [flow]);

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${cat?.color}40`,
      borderTop: `4px solid ${cat?.color}`,
      borderRadius: 8, padding: "16px 18px",
      maxHeight: 600, overflowY: "auto",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 32 }}>{cat?.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: T.text3 }}>#{String(flow.id).padStart(3, "0")}</span>
            <span style={pill(cat?.color)}>{flow.l1}</span>
            <span style={pill(T.text2)}>{flow.l2}</span>
            <span style={{
              fontFamily: FONTS.mono, fontSize: 9, fontWeight: 700,
              padding: "2px 8px", borderRadius: 4,
              background: `${sev.color}20`, color: sev.color,
            }}>SEV {flow.severity} · {sev.label}</span>
          </div>
          <div style={{ fontFamily: FONTS.header, fontSize: 15, fontWeight: 700, color: T.text }}>
            {flow.l3}
          </div>
        </div>
      </div>

      <Field label="📝 Examples"            value={flow.examples} />
      <Field label="🎯 Purpose · Source"    value={flow.source} />
      <Field label="🎯 Purpose · Destination" value={flow.destination} />

      <div style={{
        background: T.bg3, border: `1px solid ${ifT?.color}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: ifT?.color, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
          🔌 INTERFACE TYPE
        </div>
        <div style={{ fontSize: 11, color: T.text, marginBottom: 6 }}>
          {ifT?.icon} {ifT?.label}
        </div>
        <div style={{ fontSize: 9.5, color: T.text2, fontStyle: "italic" }}>
          Required: {flow.required}
        </div>
      </div>

      <div style={{
        background: T.bg3, border: `1px solid ${pT?.color}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: pT?.color, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
          📦 PAYLOAD TYPE
        </div>
        <div style={{ fontSize: 11, color: T.text, marginBottom: 6 }}>
          {pT?.label}
        </div>
        <div style={{ fontSize: 9.5, color: T.text2, fontFamily: FONTS.mono }}>
          {flow.protocol}
        </div>
      </div>

      <div style={{
        background: `${T.red}10`, border: `1px solid ${T.red}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: T.red, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
          ⚠️ IF MISSING
        </div>
        <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.5 }}>
          {flow.stops}
        </div>
      </div>

      <div style={{
        background: `${T.amber}10`, border: `1px solid ${T.amber}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: T.amber, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
          🩹 WORKAROUND
        </div>
        <div style={{ fontSize: 10, color: T.text2, fontStyle: "italic", lineHeight: 1.5 }}>
          {flow.workaround}
        </div>
      </div>

      {matchingInterfaces.length > 0 && (
        <div style={{
          background: `${T.cyan}10`, border: `1px solid ${T.cyan}30`,
          borderRadius: 6, padding: "10px 12px",
        }}>
          <EntityRefList kind="interface" ids={matchingInterfaces}
            label="🔗 Possible matching CLM interfaces"/>
          <div style={{ fontSize: 8, color: T.text3, fontStyle: "italic", marginTop: 6 }}>
            Heuristic match — verify in the LLD tab.
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 8, color: T.text3, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.5 }}>{value}</div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// MatrixView · L1 categories × severity heatmap
// ───────────────────────────────────────────────────────────────────────

function MatrixView({ setView, setL1, setSev }) {
  const severities = [5, 4, 3, 2, 1];
  const maxCount = Math.max(
    ...L1_CATEGORIES.flatMap(c => severities.map(s =>
      FLOWS.filter(f => f.l1 === c.id && f.severity === s).length
    ))
  );

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
      padding: 16, overflow: "auto",
    }}>
      <div style={{ fontSize: 10, color: T.text2, marginBottom: 12, fontStyle: "italic" }}>
        Cell intensity = count of flows. Click any cell to drill in. Darker = more flows of that severity in that L1 category.
      </div>
      <table style={{ borderCollapse: "collapse", fontSize: 10 }}>
        <thead>
          <tr>
            <th style={{ ...th, padding: "10px 14px" }}>L1 \ Severity</th>
            {severities.map(s => (
              <th key={s} style={{ ...th, padding: "10px 14px", textAlign: "center",
                                   color: SEVERITY_META[s].color }}>
                <div>S{s}</div>
                <div style={{ fontSize: 7, opacity: 0.7, marginTop: 2 }}>{SEVERITY_META[s].label}</div>
              </th>
            ))}
            <th style={{ ...th, padding: "10px 14px" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {L1_CATEGORIES.map(cat => {
            const total = FLOWS.filter(f => f.l1 === cat.id).length;
            return (
              <tr key={cat.id} style={{ borderBottom: `1px solid ${T.border2}` }}>
                <td style={{ ...td, fontWeight: 700, color: cat.color }}>
                  {cat.icon} {cat.id}
                </td>
                {severities.map(s => {
                  const n = FLOWS.filter(f => f.l1 === cat.id && f.severity === s).length;
                  const intensity = maxCount > 0 ? n / maxCount : 0;
                  return (
                    <td key={s} style={{ padding: "8px 14px", textAlign: "center" }}>
                      {n > 0 ? (
                        <button onClick={() => { setL1(cat.id); setSev(String(s)); setView("browse"); }}
                          style={{
                            background: `${SEVERITY_META[s].color}${Math.round(intensity * 80 + 15).toString(16).padStart(2, "0")}`,
                            border: `1px solid ${SEVERITY_META[s].color}80`,
                            color: T.text, fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700,
                            padding: "6px 14px", borderRadius: 4, cursor: "pointer",
                            minWidth: 36,
                          }}>
                          {n}
                        </button>
                      ) : (
                        <span style={{ color: T.text3 }}>·</span>
                      )}
                    </td>
                  );
                })}
                <td style={{ ...td, fontFamily: FONTS.mono, fontWeight: 700, color: T.text }}>{total}</td>
              </tr>
            );
          })}
          <tr style={{ borderTop: `2px solid ${T.cyan}40` }}>
            <td style={{ ...td, fontWeight: 700, color: T.cyan }}>TOTAL</td>
            {severities.map(s => (
              <td key={s} style={{
                ...td, textAlign: "center",
                fontFamily: FONTS.mono, fontWeight: 700,
                color: SEVERITY_META[s].color,
              }}>
                {FLOW_COUNTS.bySeverity[s] || 0}
              </td>
            ))}
            <td style={{ ...td, fontFamily: FONTS.mono, fontWeight: 700, color: T.cyan }}>
              {FLOW_COUNTS.total}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const selectSty = {
  background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
  padding: "5px 8px", borderRadius: 4, fontSize: 9.5, outline: "none", cursor: "pointer",
};
const th = {
  padding: "8px 10px", textAlign: "left", fontSize: 8, color: T.text2,
  fontFamily: FONTS.mono, letterSpacing: 1, textTransform: "uppercase",
  borderBottom: `1px solid ${T.border}`, background: T.bg3, fontWeight: 600,
};
const td = { padding: "8px 10px", fontSize: 10, color: T.text };
